import { ExerciseType } from '@intelingu/core/adapters/answers/AnswersAdapter';
import { CardApi } from '@intelingu/core/api/CardApi';
import { COMMON_NS } from '@intelingu/core/constants/base.constants';
import { CardType, EvaluationMark } from '@intelingu/core/interfaces/models/CardModel';
import { lazyInject } from '@intelingu/core/IoC/container';
import { AnswersStore } from '@intelingu/core/stores/cards/AnswersStore';
import { TopicCardStore } from '@intelingu/core/stores/cards/TopicCardStore';
import { CardStore } from '@intelingu/core/stores/CardStore';
import Logger from '@intelingu/core/utils/Logger';
import withTranslation from '@intelingu/core/utils/withTranslation';
import { autobind } from 'core-decorators';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { AppStore } from '../../../../stores/AppStore';
import { FormattedString } from '../../../partials/FormattedString';

export interface ExerciseCardProps {
    id: number;
    t?;
}

@withTranslation(COMMON_NS)
@observer export class ExerciseCard extends Component<ExerciseCardProps, any> {

    @lazyInject(AppStore) appStore: AppStore;
    @lazyInject(AnswersStore) answersStore: AnswersStore;
    @lazyInject(TopicCardStore) topicCardStore: TopicCardStore;
    @lazyInject(CardStore) cardStore: CardStore;
    @lazyInject(CardApi) cardApi: CardApi;

    @observable showTranslation = false;

    constructor(props: ExerciseCardProps) {
        super(props);

        this.answersStore.initialize();
    }

    componentDidMount(): void {
        window.scrollTo(0, 0);
        document.addEventListener("keydown", this.hotKeyListener);
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.hotKeyListener);
    }

    @autobind hotKeyListener(event) {
        if (!isNaN(event.key)) {
            const n = parseInt(event.key) - 1;

            if (n < 0) {
                return;
            }

            const index = this.answersStore.adapter.answers.filter((item, i) => i === n);

            if (index.length) {
                this.answer(index[0].index);
            }
        }
    }

    @action.bound async answer(index) {
        const next = await this.answersStore.answer(index);

        if (!next) {
            return;
        }

        if (this.answersStore.isAnswerError) {
            this.answersStore.mark = EvaluationMark.AGAIN;

            Logger.info('play error sound');

            // this.audioSuccess.currentTime = 0;
            // this.audioSuccess.play();
        } else {
            const audio = this.answersStore.isFinished ?
                this.appStore.audioSuccessFullRef :
                this.appStore.audioSuccessRef;

            this.appStore.audioSuccessRef.currentTime = 0;
            this.appStore.audioSuccessFullRef.currentTime = 0;

            audio.play();

            return next();
        }
    }

    @action.bound async expandTopic(e) {
        e.preventDefault();

        this.answersStore.mark = EvaluationMark.AGAIN;

        const response = await this.cardApi.getTopic(this.answersStore.card.topic.id);
        this.topicCardStore.stack.push(response.data);
    }

    @autobind _renderPlaceholder(index) {
        const { t } = this.props;
        const { currentPlaceholderIndex, isAnswerError } = this.answersStore;

        if (currentPlaceholderIndex === index) {
            return isAnswerError ?
                <span className={'error'}>{ t('trainingTryAgain') }</span> :
                <span className={'current'}><i className={'ion ion-md-help'} /></span>;
        }

        if (index < currentPlaceholderIndex) {
            return <span className={'success'}><i className={'ion ion-md-checkmark'} /></span>
        }

        if (index > currentPlaceholderIndex) {
            return <span className={'next'}>&nbsp;</span>
        }
    }

    _renderContent() {
        const { card } = this.answersStore;
        /*const { contentParts } = this.answersStore.adapter;

        return contentParts.map((content, index) => {
            const isPlaceholderExists = this.answersStore.adapter.totalPlaceholders > index;

            return <div className={'part'} key={index}>
                <div style={{ display: 'inline' }} dangerouslySetInnerHTML={{ __html: content }} />
                { isPlaceholderExists &&
                    <div className={'placeholder'}>
                        { this._renderPlaceholder(index) }
                    </div>
                }
            </div>
        });*/

        let content: string = this.answersStore.adapter.field.content;

        this.answersStore.adapter.answers.forEach((value, index) => {
            const placeholderKey = this.answersStore.adapter.type === ExerciseType.exercise ?
                '[solution]' : `[solution${index}]`;

            const placeholderString = ReactDOMServer.renderToString(
                <i className={'placeholder'}>
                    { this._renderPlaceholder(index) }
                </i>
            );
            content = content.replace(placeholderKey, placeholderString);
        });

        // return <div dangerouslySetInnerHTML={{ __html: content }} />;
        return <FormattedString value={content} />;
    }

    componentDidUpdate(prevProps: Readonly<ExerciseCardProps>, prevState: Readonly<any>, snapshot?: any): void {
        if (prevProps.id !== this.props.id) {
            this.answersStore.initialize();
        }
    }

    render() {
        if (!this.answersStore.adapter) {
            return null;
        }

        const { t } = this.props;
        const { card } = this.answersStore;
        const { answers } = this.answersStore.adapter;

        return <div className={'course-card exercise-card'}>
            <div className={'title-wrapper'}>
                <h1>{ card.topic.title } <a href={''} onClick={this.expandTopic} className={'ml-2 text-white tour-expand-topic'}><i className={'ion ion-md-expand'} /></a></h1>
            </div>

            <div className={'rules'}>
                <div className={'rule'}>
                    { this._renderContent() }
                </div>

                { !this.showTranslation && card.exercise && card.exercise.translation !== null && card.exercise.translation.length > 0 && (
                    <a href={''} onClick={e => {
                        e.preventDefault();
                        this.showTranslation = true;
                    }} className="toggle-translation">{ t('trainingShowTranslation') }</a>
                )}

                { this.showTranslation && (
                    <div className={'rule'} dangerouslySetInnerHTML={{ __html: card.exercise.translation }} />
                )}
            </div>

            <div className={'sep'} />

            <h3 className={'upper tour-choose-correct'}>{ (
                this.cardStore.currentCard.card_type === CardType.EXERCISE ? (
                    t('trainingChooseCorrectAnswer')
                ) : t('trainingChooseCorrectAnswers')
            ).toUpperCase() }</h3>

            <div className={'choices'} style={{ opacity: this.cardStore.isBufferLoaded ? 1 : 0.4 }}>
                { answers.map(({ answer, index }, i) =>
                    <button key={index} onClick={() => this.answer(index)}>{ answer } <span className={'button-index'}>({ i + 1 })</span></button>
                )}
            </div>
        </div>
    }

}
